export const DEFAULT_AVATAR = "/img/user_avatar.jpg";

export const DEFAULT_FILTERS = {
  search: "",
  genre: "",
  departement: "",
  club: "",
  licence: "",
  sort: "nameAsc",
  perPage: 25,
};

export const GENDER_OPTIONS = [
  { value: "Masculin", label: "Masculin" },
  { value: "Féminin", label: "Féminin" },
  { value: "Autre", label: "Autre" },
];

export const LICENCE_FILTER_OPTIONS = [
  { value: "", label: "Toutes licences" },
  { value: "with", label: "Avec licence" },
  { value: "without", label: "Sans licence" },
];

export function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function safeDate(value) {
  if (!value) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateFr(value) {
  const date = safeDate(value);

  if (!date) return "Non renseignée";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function formatDateShort(value) {
  const date = safeDate(value);

  if (!date) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

export function getAdherentCreatedDate(adherent) {
  return adherent?.createdAt || adherent?.created_at || null;
}

export function getAdherentName(adherent) {
  return (
    [adherent?.prenom, adherent?.nom].filter(Boolean).join(" ") || "Judoka"
  );
}

export function getAdherentPhoto(adherent) {
  return adherent?.photo_url || DEFAULT_AVATAR;
}

export function getAge(value) {
  const birthDate = safeDate(value);

  if (!birthDate) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();

  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age -= 1;
  }

  return age >= 0 ? age : null;
}

export function getAgeLabel(value) {
  const age = getAge(value);

  if (age === null) return "Âge inconnu";

  return `${age} an${age > 1 ? "s" : ""}`;
}

export function getGenreStyle(genre) {
  const normalized = normalizeText(genre);

  if (normalized === "masculin") {
    return "bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-200";
  }

  if (normalized === "feminin") {
    return "bg-pink-100 text-pink-700 dark:bg-pink-500/10 dark:text-pink-200";
  }

  if (normalized === "autre") {
    return "bg-violet-100 text-violet-700 dark:bg-violet-500/10 dark:text-violet-200";
  }

  return "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-white/60";
}

export function cleanAdherents(adherents = []) {
  return adherents.filter((adherent) => {
    return String(adherent?.nom || "").trim() && String(adherent?.prenom || "").trim();
  });
}

export function getUniqueOptions(adherents = [], key) {
  return Array.from(
    new Set(
      adherents
        .map((adherent) => String(adherent?.[key] || "").trim())
        .filter(Boolean),
    ),
  ).sort((a, b) => a.localeCompare(b, "fr"));
}

export function filterAdherents(adherents = [], filters = DEFAULT_FILTERS) {
  const search = normalizeText(filters.search);
  const genre = normalizeText(filters.genre);
  const departement = normalizeText(filters.departement);
  const club = normalizeText(filters.club);
  const licence = String(filters.licence || "");

  return adherents.filter((adherent) => {
    const name = normalizeText(getAdherentName(adherent));
    const reversedName = normalizeText(`${adherent?.nom || ""} ${adherent?.prenom || ""}`);
    const adherentClub = normalizeText(adherent?.club);
    const adherentDept = normalizeText(adherent?.departement);
    const adherentLicence = normalizeText(adherent?.licence_number);
    const adherentGenre = normalizeText(adherent?.genre);

    const matchesSearch =
      !search ||
      name.includes(search) ||
      reversedName.includes(search) ||
      adherentClub.includes(search) ||
      adherentDept.includes(search) ||
      adherentLicence.includes(search);

    const matchesGenre = !genre || adherentGenre === genre;
    const matchesDepartement = !departement || adherentDept === departement;
    const matchesClub = !club || adherentClub === club;

    const hasLicence = Boolean(String(adherent?.licence_number || "").trim());

    const matchesLicence =
      !licence ||
      (licence === "with" && hasLicence) ||
      (licence === "without" && !hasLicence);

    return (
      matchesSearch &&
      matchesGenre &&
      matchesDepartement &&
      matchesClub &&
      matchesLicence
    );
  });
}

export function sortAdherents(adherents = [], sort = "nameAsc") {
  return [...adherents].sort((a, b) => {
    const nameA = normalizeText(`${a?.nom || ""} ${a?.prenom || ""}`);
    const nameB = normalizeText(`${b?.nom || ""} ${b?.prenom || ""}`);

    const dateA = safeDate(getAdherentCreatedDate(a))?.getTime() || 0;
    const dateB = safeDate(getAdherentCreatedDate(b))?.getTime() || 0;

    const ageA = getAge(a?.date_naissance) ?? 999;
    const ageB = getAge(b?.date_naissance) ?? 999;

    if (sort === "nameDesc") return nameB.localeCompare(nameA, "fr");
    if (sort === "recent") return dateB - dateA;
    if (sort === "oldest") return dateA - dateB;
    if (sort === "ageAsc") return ageA - ageB;
    if (sort === "ageDesc") return ageB - ageA;

    return nameA.localeCompare(nameB, "fr");
  });
}

export function paginateItems(items = [], page = 1, perPage = 25) {
  const safePerPage = Number(perPage || 25);
  const totalPages = Math.max(Math.ceil(items.length / safePerPage), 1);
  const safePage = Math.min(Math.max(Number(page || 1), 1), totalPages);
  const start = (safePage - 1) * safePerPage;

  return {
    page: safePage,
    totalPages,
    items: items.slice(start, start + safePerPage),
  };
}

export function buildAdherentStats(adherents = []) {
  const stats = {
    total: adherents.length,
    masculin: 0,
    feminin: 0,
    autre: 0,
    withLicence: 0,
    withoutLicence: 0,
    minors: 0,
    adults: 0,
    clubs: getUniqueOptions(adherents, "club").length,
    departements: getUniqueOptions(adherents, "departement").length,
  };

  adherents.forEach((adherent) => {
    const genre = normalizeText(adherent?.genre);
    const hasLicence = Boolean(String(adherent?.licence_number || "").trim());
    const age = getAge(adherent?.date_naissance);

    if (genre === "masculin") stats.masculin += 1;
    else if (genre === "feminin") stats.feminin += 1;
    else stats.autre += 1;

    if (hasLicence) stats.withLicence += 1;
    else stats.withoutLicence += 1;

    if (age !== null && age < 18) stats.minors += 1;
    if (age !== null && age >= 18) stats.adults += 1;
  });

  return stats;
}